import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, Calendar, CheckCircle2, Eye, FileText, Mail, Phone, Shield,
  Target, User, UserPlus, UsersRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { getAgent, getEmployee } from '../../lib/api';

const statusColors = {
  Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Inactive: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  'On Leave': 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
};

const statusApiToDisplay = (value) => {
  if (!value) return 'Active';
  const normalized = String(value).toLowerCase();
  if (normalized === 'inactive') return 'Inactive';
  if (normalized === 'suspended') return 'On Leave';
  return 'Active';
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getRoleBase = (pathname) => {
  if (pathname.startsWith('/whitelabel')) return '/whitelabel';
  if (pathname.startsWith('/employee')) return '/employee';
  return '/admin';
};

const InfoCard = ({ icon: Icon, label, value, valueClassName = '' }) => (
  <div className="rounded-xl border p-4">
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </div>
    <div className={`mt-2 text-sm font-semibold break-words ${valueClassName}`}>{value || '—'}</div>
  </div>
);

export const ViewAgentPage = () => {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const basePath = useMemo(() => getRoleBase(location.pathname), [location.pathname]);

  useEffect(() => {
    const load = async () => {
      if (!token || !id) return;
      setLoading(true);
      try {
        const resp = await getAgent(token, id);
        setAgent(resp?.data || null);
      } catch (err) {
        toast.error(err?.message || 'Failed to load agent details');
        navigate(`${basePath}/agents`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, id, navigate, basePath]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading agent details...</div>;
  if (!agent) return null;

  const status = statusApiToDisplay(agent.status);
  const initials = String(agent.full_name || '')
    .split(' ')
    .map((part) => part[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="space-y-5 max-w-7xl w-full">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Eye className="h-6 w-6 text-blue-600" />
          <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">Agent Profile</h1>
        </div>
        <Button variant="outline" onClick={() => navigate(`${basePath}/agents`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>

      <div className="rounded-xl border p-6 shadow-sm space-y-6">
        <div className="flex items-center gap-4 rounded-xl bg-muted/40 p-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-lg font-bold text-white">
            {initials}
          </div>
          <div>
            <div className="text-lg font-semibold">{agent.full_name}</div>
            <div className="mt-1 flex items-center gap-2">
              <Badge className={statusColors[status] || statusColors.Active}>{status}</Badge>
              <span className="text-xs text-muted-foreground">{agent.id}</span>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard icon={Phone} label="Phone" value={agent.phone} />
          <InfoCard icon={Mail} label="Email" value={agent.email} />
          <InfoCard icon={Calendar} label="Joined" value={formatDate(agent.created_at)} />
          {(user?.role === 'super_admin' || user?.role === 'wl_admin') && (
          <InfoCard icon={UserPlus} label="Created By" value={agent.created_by_name} valueClassName="text-blue-600" />
          )}
          <InfoCard icon={FileText} label="Loan Applications" value={String(agent.created_loan_applications_count || 0)} />
          <InfoCard icon={User} label="Total Leads" value={String(agent.created_leads_count || 0)} />
          <InfoCard icon={CheckCircle2} label="Converted Leads" value={String(agent.converted_leads_count || 0)} valueClassName="text-emerald-600" />
          <InfoCard icon={Target} label="Monthly Target" value={String(agent.target || 0)} />
        </div>
      </div>
    </div>
  );
};

export const ViewEmployeePage = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const basePath = useMemo(() => getRoleBase(location.pathname), [location.pathname]);

  useEffect(() => {
    const load = async () => {
      if (!token || !id) return;
      setLoading(true);
      try {
        const resp = await getEmployee(token, id);
        setEmployee(resp?.data || null);
      } catch (err) {
        toast.error(err?.message || 'Failed to load employee details');
        navigate(`${basePath}/employees`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, id, navigate, basePath]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading employee details...</div>;
  if (!employee) return null;

  const status = statusApiToDisplay(employee.status);
  const initials = String(employee.full_name || '')
    .split(' ')
    .map((part) => part[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const performance = (Number(employee.created_leads_count) || 0) > 0
    ? Math.round(((Number(employee.converted_leads_count) || 0) / (Number(employee.created_leads_count) || 1)) * 100)
    : 0;

  return (
    <div className="space-y-5 max-w-7xl w-full">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-blue-600" />
          <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">Employee Profile</h1>
        </div>
        <Button variant="outline" onClick={() => navigate(`${basePath}/employees`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>

      <div className="rounded-xl border p-6 shadow-sm space-y-6">
        <div className="flex items-center gap-4 rounded-xl bg-muted/40 p-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-lg font-bold text-white">
            {initials}
          </div>
          <div>
            <div className="text-lg font-semibold">{employee.full_name}</div>
            <div className="mt-1 flex items-center gap-2">
              <Badge className={statusColors[status] || statusColors.Active}>{status}</Badge>
              <span className="text-xs text-muted-foreground">{employee.id}</span>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard icon={Phone} label="Phone" value={employee.phone} />
          <InfoCard icon={Mail} label="Email" value={employee.email} />
          <InfoCard icon={Briefcase} label="Role" value={employee.role_name} />
          <InfoCard icon={User} label="Department" value={employee.department_name} />
          <InfoCard icon={Calendar} label="Joined" value={formatDate(employee.created_at)} />
          <InfoCard icon={UsersRound} label="Created Agents" value={String(employee.created_agents_count || 0)} />
          <InfoCard icon={FileText} label="Loan Applications" value={String(employee.created_loan_applications_count || 0)} />
          <InfoCard icon={Target} label="Assigned Leads" value={String(employee.assigned_leads_count || 0)} />
          <InfoCard icon={CheckCircle2} label="Assigned Loans" value={String(employee.assigned_loans_count || 0)} valueClassName="text-emerald-600" />
          <InfoCard icon={Shield} label="Performance" value={`${performance}%`} valueClassName={performance >= 80 ? 'text-emerald-600' : 'text-amber-600'} />
        </div>
      </div>
    </div>
  );
};
